import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { HasRoleDirective } from '@core/directives/has-role.directive';
import {
  FaIconComponent,
  FaIconLibrary,
} from '@fortawesome/angular-fontawesome';
import {
  faBuildingShield,
  faChartLine,
  faCircleNodes,
  faCodeMerge,
  faFileContract,
  faFingerprint,
  faGaugeHigh,
  faGear,
  faGears,
  faImages,
  faLayerGroup,
  faList,
  faLock,
  faServer,
  faSliders,
  faStethoscope,
  faTags,
  faUserGroup,
  faEnvelope,
  faHouse,
} from '@fortawesome/free-solid-svg-icons';
import { IsFeatureEnabledDirective } from '@core/feature-preview/directives/is-feature-enabled.directive';
import { SidebarService } from '@core/components/sidebar/service/sidebar.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { SidebarItemComponent } from '@core/components/sidebar/components/sidebar-item/sidebar-item.component';
import { SidebarLogoComponent } from '@core/components/sidebar/components/logo/sidebar-logo.component';
import { SidebarFooterComponent } from '@core/components/sidebar/components/footer/sidebar-footer.component';
import { UserRoleType } from '@corezilla/api';
import { SidebarUserMenuComponent } from '@core/components/sidebar/components/user-menu/sidebar-user-menu.component';
import { FlowpilotComponent } from '@core/components/sidebar/components/flowpilot/flowpilot.component';
import { Store } from '@ngrx/store';
import { selectHasRole } from '@core/states/auth/auth.selectors';

@Component({
  selector: 'app-sidebar-content',
  imports: [
    CommonModule,
    FaIconComponent,
    HasRoleDirective,
    IsFeatureEnabledDirective,
    SidebarItemComponent,
    SidebarLogoComponent,
    SidebarFooterComponent,
    SidebarUserMenuComponent,
    FlowpilotComponent,
  ],
  template: `
    <div
      class="chrome--bg chrome--border flex h-full flex-col border-r"
      aria-label="Sidebar">
      <div class="flex h-full flex-col gap-y-2.5 py-2">
        <app-sidebar-logo
          class="flex w-full overflow-hidden px-3"
          [collapsed]="!!collapsed()" />
        <div
          class="flex grow flex-col items-center gap-y-6 overflow-y-auto px-3 font-medium">
          <div class="flex w-full flex-col">
            <ul>
              <app-sidebar-item
                [icon]="['fas', 'chart-line']"
                label="Dashboard"
                path="dashboard" />
              <app-sidebar-item
                [icon]="['fas', 'circle-nodes']"
                label="Workflows"
                path="workflows" />
              <app-sidebar-item
                [icon]="['fas', 'building-shield']"
                label="Access Hub"
                path="tenants" />
              <app-sidebar-item
                [icon]="['fas', 'code-merge']"
                label="Processes"
                path="process-definitions" />
              <app-sidebar-item
                [icon]="['fas', 'fingerprint']"
                label="Triggers"
                path="detection-definitions" />
              <app-sidebar-item
                *appIsFeatureEnabled="'OCR'"
                [icon]="['fas', 'file-contract']"
                label="OCR"
                path="ocr-templates" />
              <app-sidebar-item
                [icon]="['fas', 'list']"
                label="Changelogs"
                path="global-changelogs" />
            </ul>
          </div>

          <div class="flex w-full flex-col">
            <div
              class="flex gap-2 text-sm leading-6 uppercase"
              [class.visible]="!collapsed()"
              [class.pl-3]="!collapsed()"
              [class.invisible]="collapsed()">
              <span class="text--contrast-low truncate">
                {{ collapsed() ? 'R' : 'Runs' }}
              </span>
            </div>
            <ul>
              <!--
                Same icons as "Processes" and "Triggers" above, so the
                connection between a definition and its runs is visible.
              -->
              <app-sidebar-item
                [icon]="['fas', 'code-merge']"
                label="Process Runs"
                path="process-instances" />
              <app-sidebar-item
                [icon]="['fas', 'fingerprint']"
                label="Trigger Runs"
                path="detection-instances" />
            </ul>
          </div>

          <div class="flex w-full flex-col">
            <div
              class="flex gap-2 text-sm leading-6 uppercase"
              [class.visible]="!collapsed()"
              [class.pl-3]="!collapsed()"
              [class.invisible]="collapsed()">
              <span class="text--contrast-low truncate">
                {{ collapsed() ? 'S' : 'Settings' }}
              </span>
            </div>
            <ul>
              <!-- MCP tool permissions are managed by Super Admin and Developer only (plan permission E11) -->
              @if (managesPermissions()) {
                <app-sidebar-item
                  [icon]="['fas', 'gears']"
                  label="Application Settings"
                  path="users"
                  [subPages]="[
                    { label: 'Users', path: 'users' },
                    { label: 'MCP tools', path: 'application-settings' },
                  ]" />
              } @else {
                <app-sidebar-item
                  [icon]="['fas', 'user-group']"
                  label="Users"
                  path="users" />
              }
              <app-sidebar-item
                [icon]="['fas', 'tags']"
                label="Tags"
                path="tags" />
            </ul>
          </div>

          <div
            class="flex w-full flex-col"
            *appHasRole="[Role.ROLE_SUPER_ADMIN]">
            <div
              class="flex gap-1.5 text-sm leading-6 uppercase"
              [class.visible]="!collapsed()"
              [class.pl-3]="!collapsed()"
              [class.invisible]="collapsed()">
              <span class="text--contrast-low truncate">
                {{ collapsed() ? 'S' : 'Staff' }}
              </span>
              <fa-icon [icon]="'lock'" class="text--contrast-low text-xs" />
            </div>
            <ul>
              <app-sidebar-item
                [icon]="['fas', 'gear']"
                label="System Configuration"
                path="application-configurations" />
              <app-sidebar-item
                [icon]="['fas', 'layer-group']"
                label="Components"
                path="component-definitions" />
              <app-sidebar-item
                [icon]="['fas', 'stethoscope']"
                label="Trigger Types"
                path="detection-type-definitions" />
              <app-sidebar-item
                [icon]="['fas', 'images']"
                label="Assets"
                path="assets" />
              <app-sidebar-item
                [icon]="['fas', 'sliders']"
                label="Setting Definitions"
                path="setting-definitions" />
              <app-sidebar-item
                [icon]="['fas', 'gauge-high']"
                label="Process Throttles"
                path="process-throttles" />
              <app-sidebar-item
                [icon]="['fas', 'server']"
                label="Message Workers"
                path="message-worker-configs" />
            </ul>
          </div>
        </div>

        <div class="flex flex-col gap-y-3">
          <app-flowpilot
            class="px-[13px]"
            *appIsFeatureEnabled="'FlowPilotFloatingWindow'"
            [collapsed]="!!collapsed()" />

          <app-sidebar-user-menu class="px-[7px]" [collapsed]="!!collapsed()" />

          <app-sidebar-footer class="px-3" [collapsed]="!!collapsed()" />
        </div>
      </div>
    </div>
  `,
})
export class SidebarContentComponent {
  /** INJECTIONS */
  /** Reference to icon library **/
  private readonly library: FaIconLibrary = inject(FaIconLibrary);

  /** Reference to sidebar service **/
  private readonly sidebarService: SidebarService = inject(SidebarService);

  /** Reference to the store, for the role of the current user **/
  private readonly store = inject(Store);

  /** PROPERTIES **/
  /** Holds role user types **/
  protected readonly Role = UserRoleType;

  /** True for Super Admin and Developer: they get Application Settings with the MCP tools **/
  protected readonly managesPermissions = this.store.selectSignal(
    selectHasRole([UserRoleType.ROLE_SUPER_ADMIN, UserRoleType.DEVELOPER])
  );

  /** Holds the value if sidebar is collapsed or not **/
  protected readonly collapsed = toSignal(
    this.sidebarService.sidebarCollapsed$
  );

  constructor() {
    this.library.addIcons(
      faHouse,
      faChartLine,
      faCircleNodes,
      faBuildingShield,
      faCodeMerge,
      faList,
      faTags,
      faGear,
      faFingerprint,
      faGears,
      faUserGroup,
      faLock,
      faLayerGroup,
      faStethoscope,
      faImages,
      faGaugeHigh,
      faFileContract,
      faEnvelope,
      faServer,
      faSliders
    );
  }
}
